import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';
import Icon from './Icon';


function TechniciansManager({ companyId, darkMode = true }) {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTechnician, setCurrentTechnician] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    specializations: [],
    is_active: true
  });
  const [newSpecialization, setNewSpecialization] = useState('');

  useEffect(() => {
    fetchTechnicians();
  }, [companyId]);

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getTechnicians(false, companyId);
      setTechnicians(response.data.technicians || []);
      setError('');
    } catch (err) {
      setError('Failed to load technicians');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate if password provided, username must also be provided
    if (formData.password && !formData.username) {
      setError('Username is required when providing a password');
      return;
    }
    if (formData.username && !formData.password) {
      setError('Password is required when providing a username');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const data = {
        name: formData.name,
        email: formData.email?.trim() || null,  // Send null for empty emails
        phone: formData.phone?.trim() || null,
        specializations: formData.specializations,
        is_active: formData.is_active,
        company_id: companyId,
        // Include username/password for creating user account
        username: formData.username || null,
        password: formData.password || null
      };

      const response = await bookingAPI.createTechnician(data);
      let successMsg = 'Technician created successfully!';
      if (formData.username && response.data.login_username) {
        successMsg = `Technician created successfully!\n\nLogin username: ${response.data.login_username}`;
      } else if (formData.username) {
        successMsg = 'Technician created successfully! Login account created.';
      }
      setSuccess(successMsg);
      setShowAddModal(false);
      resetForm();
      fetchTechnicians();
      setTimeout(() => setSuccess(''), 10000); // Show longer so user can read the username
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create technician');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        specializations: formData.specializations,
        is_active: formData.is_active
      };

      await bookingAPI.updateTechnician(currentTechnician.id, data);
      setSuccess('Technician updated successfully!');
      setShowEditModal(false);
      resetForm();
      fetchTechnicians();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update technician');
    }
  };

  const handleDeactivate = async (technician) => {
    try {
      await bookingAPI.updateTechnician(technician.id, {
        ...technician,
        is_active: !technician.is_active
      });
      setSuccess(`Technician ${technician.is_active ? 'deactivated' : 'activated'}!`);
      fetchTechnicians();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to modify technician');
    }
  };

  const handleDelete = async (technicianId) => {
    if (!window.confirm('Are you sure you want to delete this technician? If they have assigned bookings, deletion is not possible.')) {
      return;
    }

    try {
      await bookingAPI.deleteTechnician(technicianId);
      setSuccess('Technician deleted!');
      fetchTechnicians();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete technician');
    }
  };

  const openEditModal = (technician) => {
    setCurrentTechnician(technician);
    setFormData({
      name: technician.name || '',
      email: technician.email || '',
      phone: technician.phone || '',
      specializations: technician.specializations || [],
      is_active: technician.is_active !== undefined ? technician.is_active : true
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      username: '',
      password: '',
      specializations: [],
      is_active: true
    });
    setNewSpecialization('');
    setCurrentTechnician(null);
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !formData.specializations.includes(newSpecialization.trim())) {
      setFormData({
        ...formData,
        specializations: [...formData.specializations, newSpecialization.trim()]
      });
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (spec) => {
    setFormData({
      ...formData,
      specializations: formData.specializations.filter(s => s !== spec)
    });
  };

  if (loading) {
    return <div className="loading">Loading technicians...</div>;
  }

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <h2 style={{color: '#ffffff', margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px'}}>
          <Icon name="technician" color="brand" darkMode={true} size={20} style={{ verticalAlign: 'middle' }} />
          Technicians
        </h2>
        <button 
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }} 
          className="btn btn-primary"
        >
          + Add Technician
        </button>
      </div>

      {error && <div className="error" style={{marginBottom: '16px'}}>{error}</div>}
      {success && (
        <div className="success" style={{
          marginBottom: '16px',
          padding: '16px',
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px'
        }}>
          {success}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {technicians.length === 0 ? (
          <p style={{padding: '20px', textAlign: 'center', color: 'var(--color-gray)'}}>
            No technicians yet. Add your first technician to get started!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {technicians.map((technician, idx) => (
              <div key={technician.id} style={{
                padding: '16px',
                borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {/* Top row: Name + Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '15px', color: '#fff' }}>{technician.name}</strong>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      backgroundColor: technician.is_active ? '#7B9663' : '#9e9e9e',
                      flexShrink: 0
                    }}
                  >
                    {technician.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Contact info row */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                  {technician.email && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="email" color="secondary" darkMode={true} size={14} />
                      {technician.email}
                    </span>
                  )}
                  {technician.phone && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="phone" color="secondary" darkMode={true} size={14} />
                      {technician.phone}
                    </span>
                  )}
                  {technician.login_username ? (
                    <span style={{
                      fontSize: '12px', 
                      color: '#7B9663',
                      background: 'rgba(61, 90, 32, 0.15)',
                      padding: '3px 10px',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Icon name="key" color="success" darkMode={true} size={12} />
                      {technician.login_username}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: '11px', 
                      color: '#999',
                      fontStyle: 'italic'
                    }}>
                      No login account
                    </span>
                  )}
                </div>

                {/* Specializations */}
                {technician.specializations && technician.specializations.length > 0 && (
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                    {technician.specializations.map((spec, specIdx) => (
                      <span
                        key={specIdx}
                        style={{
                          padding: '3px 10px',
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          borderRadius: '12px',
                          fontSize: '11px',
                          color: '#60a5fa'
                        }}
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                  <button
                    onClick={() => openEditModal(technician)}
                    className="btn btn-secondary btn-sm"
                    style={{padding: '6px 14px', fontSize: '12px', minHeight: '36px'}}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeactivate(technician)}
                    className="btn btn-sm"
                    style={{
                      padding: '6px 14px',
                      fontSize: '12px',
                      minHeight: '36px',
                      background: technician.is_active ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                      color: technician.is_active ? '#f87171' : '#4A8C18',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {technician.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(technician.id)}
                    className="btn btn-danger btn-sm"
                    style={{padding: '6px 14px', fontSize: '12px', minHeight: '36px'}}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Technician Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          resetForm();
        }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a' }}>
                <Icon name="technician" color="brand" darkMode={darkMode} size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Add New Technician
              </h2>
              <button className="close-btn" onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}>×</button>
            </div>
            
            <form onSubmit={handleAdd}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px'}}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Technician's full name"
                  />
                </div>

                <div className="form-group">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="technician@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone (optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      // Only allow numbers, spaces, plus, and dashes
                      const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                      setFormData({...formData, phone: value});
                    }}
                    placeholder="+44 7911 123456"
                  />
                </div>
              </div>

              {/* Login Credentials Section */}
              <div style={{
                marginTop: '20px',
                marginBottom: '20px',
                padding: '20px',
                background: 'rgba(61, 90, 32, 0.08)',
                border: '1px solid rgba(61, 90, 32, 0.2)',
                borderRadius: '12px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#7B9663', fontSize: '15px' }}>
                  <Icon name="key" color="success" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Create Login Account
                </h4>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
                  If you provide a username and password, the technician will be able to log in and see their assigned jobs.
                </p>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Username</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      placeholder="e.g. john_smith"
                      autoComplete="off"
                    />
                    {formData.username && (
                      <small style={{ 
                        color: '#7B9663', 
                        fontSize: '12px', 
                        marginTop: '6px', 
                        display: 'block',
                        background: 'rgba(61, 90, 32, 0.2)',
                        padding: '8px 12px',
                        borderRadius: '8px'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon name="lightbulb" color="info" darkMode={true} size={14} />
                          Login username: <strong style={{fontFamily: 'monospace'}}>companyname_{formData.username}</strong>
                        </span>
                      </small>
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Password</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Min 6 characters"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Specializations (optional)</label>
                <div style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                  <input
                    type="text"
                    value={newSpecialization}
                    onChange={(e) => setNewSpecialization(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSpecialization();
                      }
                    }}
                    placeholder="e.g. E-bike, Suspension, Brakes"
                    style={{flex: 1}}
                  />
                  <button
                    type="button"
                    onClick={addSpecialization}
                    className="btn btn-secondary btn-sm"
                    style={{ whiteSpace: 'nowrap', minWidth: '60px' }}
                  >
                    Add
                  </button>
                </div>
                {formData.specializations.length > 0 && (
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                    {formData.specializations.map((spec, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          borderRadius: '999px',
                          fontSize: '12px',
                          color: '#60a5fa',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {spec}
                        <button
                          type="button"
                          onClick={() => removeSpecialization(spec)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#60a5fa',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: 0,
                            width: '16px',
                            height: '16px'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group" style={{
                padding: '12px 16px',
                background: formData.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: formData.is_active ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                marginTop: '8px'
              }}>
                <label style={{display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', color: formData.is_active ? '#7B9663' : '#f87171' }}>
                      {formData.is_active ? (
                        <>
                          <Icon name="success" color="success" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Active Technician
                        </>
                      ) : (
                        <>
                          <Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Inactive Technician
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
                      {formData.is_active
                        ? 'The technician is available for booking and appears in the booking calendar.'
                        : 'The technician does not accept bookings and does not appear in the calendar.'}
                    </div>
                  </div>
                </label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  <Icon name="success" color="primary" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Create Technician
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Technician Modal */}
      {showEditModal && currentTechnician && (
        <div className="modal-overlay" onClick={() => {
          setShowEditModal(false);
          resetForm();
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: darkMode ? '#ffffff' : '#1a1a1a' }}>
                <Icon name="edit" color="primary" darkMode={darkMode} size={20} />
                Edit Technician
              </h2>
              <button className="close-btn" onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}>×</button>
            </div>
            
            {/* Show login info if exists */}
            {currentTechnician.login_username && (
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: 'rgba(61, 90, 32, 0.1)',
                border: '1px solid rgba(61, 90, 32, 0.2)',
                borderRadius: '10px'
              }}>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
                  Login username:
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  fontFamily: 'monospace', 
                  color: '#7B9663',
                  fontWeight: '600'
                }}>
                  <Icon name="key" color="success" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  {currentTechnician.login_username}
                </div>
              </div>
            )}
            
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email (optional)</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="technician@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    // Only allow numbers, spaces, plus, and dashes
                    const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                    setFormData({...formData, phone: value});
                  }}
                  placeholder="+44 7911 123456"
                />
              </div>

              <div className="form-group">
                <label>Specializations</label>
                <div style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                  <input
                    type="text"
                    value={newSpecialization}
                    onChange={(e) => setNewSpecialization(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSpecialization();
                      }
                    }}
                    placeholder="e.g. E-bike, Suspension"
                    style={{flex: 1}}
                  />
                  <button
                    type="button"
                    onClick={addSpecialization}
                    className="btn btn-secondary btn-sm"
                    style={{ whiteSpace: 'nowrap', minWidth: '60px' }}
                  >
                    Add
                  </button>
                </div>
                {formData.specializations.length > 0 && (
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                    {formData.specializations.map((spec, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: '#60a5fa',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {spec}
                        <button
                          type="button"
                          onClick={() => removeSpecialization(spec)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#60a5fa',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: 0,
                            width: '16px',
                            height: '16px'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group" style={{
                padding: '12px 16px',
                background: formData.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: formData.is_active ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                marginTop: '8px'
              }}>
                <label style={{display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', color: formData.is_active ? '#7B9663' : '#f87171' }}>
                      {formData.is_active ? (
                        <>
                          <Icon name="success" color="success" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Active Technician
                        </>
                      ) : (
                        <>
                          <Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Inactive Technician
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
                      {formData.is_active
                        ? 'The technician is available for booking and appears in the booking calendar.'
                        : 'The technician does not accept bookings and does not appear in the calendar.'}
                    </div>
                  </div>
                </label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="save" color="primary" darkMode={true} size={16} />
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechniciansManager;









